import React from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link } from "react-router-dom";
import { ReactComponent as BuildingsFill } from "bootstrap-icons/icons/buildings-fill.svg";
import { ReactComponent as BoxSeam } from "bootstrap-icons/icons/box-seam.svg";
import { ReactComponent as People } from "bootstrap-icons/icons/people.svg";
import Spinner from "../spinner/Spinner";
import { useHeadOffices } from "../../hooks/useHeadOffice";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";

const HeadOfficeDashboard = () => {
  const { data, isLoading, error } = useHeadOffices();

  // console.log("Dashboard - Full response:", data);
  // console.log("Dashboard - Is Loading:", isLoading);
  // console.log("Dashboard - Error:", error);

  const headOffices = data?.data || [];
  // console.log("Dashboard - Head Offices:", headOffices);

  const totalHeadOffices = headOffices.length;
  const activeHeadOffices = headOffices.filter((ho) => ho.is_activated).length;
  const totalBranches = headOffices.reduce((sum, ho) => sum + (ho.total_branches || 0), 0);
  const activeBranches = headOffices.reduce((sum, ho) => sum + (ho.active_branches || 0), 0);

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Head Office Management</h1>
          <Link
            to="/head-office/list"
            className="bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition"
          >
            View All Head Offices
          </Link>
        </div>

        {isLoading ? (
          <Spinner loading={isLoading} />
        ) : error ? (
          <div className="text-red-500 bg-red-50 border border-red-200 rounded p-4">
            <h3 className="font-bold mb-2">Error loading dashboard</h3>
            <p>{error?.message || "An error occurred"}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Head Offices</p>
                <h3 className="text-2xl font-bold mt-2">{totalHeadOffices}</h3>
              </div>
              <BuildingsFill className="w-10 h-10 text-orange1" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Active Head Offices</p>
                <h3 className="text-2xl font-bold mt-2 text-green-600">{activeHeadOffices}</h3>
              </div>
              <BuildingsFill className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Branches</p>
                <h3 className="text-2xl font-bold mt-2">{totalBranches}</h3>
              </div>
              <People className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Active Branches</p>
                <h3 className="text-2xl font-bold mt-2 text-green-600">{activeBranches}</h3>
              </div>
              <People className="w-10 h-10 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Recent Head Offices</h2>
          </div>
          {headOffices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 mb-4">No head offices found</p>
              <Link
                to="/head-office/create"
                className="inline-block bg-orange1 text-white px-6 py-2 rounded hover:bg-orange-600 transition"
              >
                Create First Head Office
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branches
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {headOffices.slice(0, 5).map((headOffice) => (
                  <tr key={headOffice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{headOffice.name}</div>
                      <div className="text-sm text-gray-500">{headOffice.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {headOffice.city}, {headOffice.state}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {headOffice.active_branches}/{headOffice.total_branches}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span style={{
                        padding: "2px 10px",
                        borderRadius: "9999px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        backgroundColor: headOffice.status === "active" ? "#dcfce7" : headOffice.status === "suspended" ? "#fef9c3" : "#fee2e2",
                        color: headOffice.status === "active" ? "#166534" : headOffice.status === "suspended" ? "#854d0e" : "#991b1b",
                      }}>
                        {headOffice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span style={{
                        padding: "2px 10px",
                        borderRadius: "9999px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        backgroundColor: headOffice.is_activated ? "#dbeafe" : "#f3f4f6",
                        color: headOffice.is_activated ? "#1e40af" : "#4b5563",
                      }}>
                        {headOffice.is_activated ? "Activated" : "Not Activated"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/head-office/details?id=${headOffice.id}`}
                        className="text-orange1 hover:text-orange-600"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </MainLayouts>
  );
};

export default HeadOfficeDashboard;
